import Components from 'formiojs/components/Components';
import Validator from 'formiojs/validator/Validator';
import { UrlHelper } from 'src/utils/UrlHelper';

const FileComponent = (Components as any).components.file;

class File extends (FileComponent as any) {
    /**
     * Returns the URL used for file display (link href) and download.
     * Override this method to transform the URL after upload (e.g. use a CDN, proxy, or add query params).
     * @param fileInfo - File object from dataValue (has url, name, storage, etc.)
     * @returns URL string to use for the file link and download
     */
    getDownloadUrl(fileInfo: any): string {
        return fileInfo?.data?.file?.url ?? '#';
    }

    getFile(fileInfo: any) {
        const { fileService, component } = this as any;
        if (!fileService) {
            console.warn('File Service not provided');

            return;
        }
        const options = component?.options ?? {};
        const fileInfoToUse = component?.privateDownload
            ? { ...fileInfo, private: true }
            : fileInfo;
        fileService
            .downloadFile(fileInfoToUse, options)
            .then((file: any) => {
                if (file) {
                    UrlHelper.downloadFile(
                        file.url,
                        file.originalName || file.name,
                    );
                }
            })
            .catch((response: any) => {
                console.error(response);
            });
    }

    attach(element: any) {
        this.loadRefs(element, {
            fileDrop: 'single',
            fileBrowse: 'single',
            galleryButton: 'single',
            cameraButton: 'single',
            takePictureButton: 'single',
            toggleCameraMode: 'single',
            videoPlayer: 'single',
            fileLink: 'multiple',
            removeLink: 'multiple',
            fileStatusRemove: 'multiple',
            fileImage: 'multiple',
            fileType: 'multiple',
            fileProcessingLoader: 'single',
        });

        this.refs?.removeLink?.forEach((removeLink: any, index: number) => {
            this.addEventListener(removeLink, 'click', (event: any) => {
                event.preventDefault();
                event.stopPropagation();
                const fileInfo = this.dataValue[index];

                this.deleteFile(fileInfo);
                this.splice(index);
                this.redraw();
            });
        });

        this.refs.fileStatusRemove.forEach(
            (fileStatusRemove: any, index: number) => {
                this.addEventListener(
                    fileStatusRemove,
                    'click',
                    (event: any) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (this.abortUpload) {
                            this.abortUpload();
                        }
                        this.statuses.splice(index, 1);
                        if (!this.statuses.length) {
                            this.refs.fileDrop.removeAttribute('hidden');
                        }
                        this.redraw();
                    },
                );
            },
        );

        return super.attach(element);
    }

    checkCustomValidityBeforeUpload(currentFilesData: Record<string, unknown>) {
        const newInstance = new FileComponent(this.schema);

        newInstance.dataValue = [
            ...this.getValue(),
            ...Object.values(currentFilesData),
        ];

        return (
            Validator.checkComponent(
                newInstance,
                this.data,
                this.row,
                true,
                false,
            )?.filter(
                (item: { context: { validator: string } }) =>
                    item?.context?.validator === 'custom',
            )?.length === 0
        );
    }

    checkComponentCustomValidity(currentFilesData: Record<string, unknown>) {
        const newInstance = new FileComponent(this.schema);

        newInstance.dataValue = [
            ...this.getValue(),
            ...Object.values(currentFilesData),
        ];

        const check = Validator.checkComponent(
            newInstance,
            this.data,
            this.row,
            true,
            false,
        )?.filter(
            (item: { context: { validator: string } }) =>
                item?.context?.validator === 'custom',
        );
        const validations = check;

        return this.setCustomValidity(validations, true);
    }

    upload(files: Record<string, unknown>) {
        const isValid = this.checkCustomValidityBeforeUpload(files);

        if (!isValid) {
            this.checkComponentCustomValidity(files);
        } else {
            super.upload(files);
        }
    }

    checkValidity(
        data: any,
        dirty: any,
        row: any,
        silentCheck: boolean,
    ): boolean {
        const isValid = super.checkValidity(data, dirty, row, silentCheck);
        if (!isValid) {
            return false;
        }

        const errorStatuses = this.statuses?.filter(
            (status: any) => status?.status === 'error',
        );
        if (errorStatuses?.length > 0) {
            if (!silentCheck) {
                this.setCustomValidity(
                    errorStatuses.map((status: any) => ({
                        message: status.message,
                        context: { validator: status.validator },
                    })),
                    true,
                );
            }

            return false;
        }

        if (!silentCheck && this.error?.context?.validator === 'fileSize') {
            this.setCustomValidity('');
        }

        return true;
    }
}

export default File;
